import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { tokens } from "../tokens";
import { useBreakpoint } from "../hooks/useBreakpoint";

/* ── Brand constants — copied 1:1 from JiraniDeals.jsx so this section
       is visually identical to the carousels above it ── */
const GREEN      = "#1e9254";
const GREEN_DARK = "#155c36";
const AMBER      = "#D97706";
const STAR_GOLD  = "#F0A93C";
const SERIF      = "'Instrument Serif', Georgia, serif";
const SANS       = tokens?.font?.family?.sans || "'DM Sans', -apple-system, sans-serif";
const BORDER     = "#E5E7EB";
const FAINT      = "#8a93a8";
const SECTION_BG = "#F7F8FA"; // matches JiraniDeals / ProFeed's T.bg exactly

if (typeof document !== "undefined" && !document.getElementById("__jirani_deals_fonts")) {
  const l = document.createElement("link");
  l.id = "__jirani_deals_fonts";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap";
  document.head.appendChild(l);
}

/* ── Image pools per category (swap for real pro photos from Supabase) ── */
const IMG = {
  childcare: [
    "https://images.unsplash.com/photo-1587654780291-39c9404d746b?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1516627145497-ae6968895b74?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1560785496-3c9d27877182?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1543342384-1f1350e27861?w=340&h=280&fit=crop",
  ],
  tutoring: [
    "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=340&h=280&fit=crop",
  ],
  chef: [
    "https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1577106263724-2c8e03bde86b?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1544025162-d76694265947?w=340&h=280&fit=crop",
    "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=340&h=280&fit=crop",
  ],
};

const LOCATIONS = [
  "Nyali", "Westlands", "Bamburi", "Kilimani", "Tudor", "Kasarani",
  "Likoni", "Embakasi", "Mtwapa", "Lang'ata", "Shanzu", "South B",
  "Nakuru", "Kisumu", "Naivasha", "Milimani", "Thika", "Juja", "Meru", "Eldoret",
];

/* Three non-overlapping name pools, none reusing names already shown
   elsewhere on the homepage (hero strip, GENERAL_NAMES, RAIN_NAMES in
   JiraniDeals.jsx). Each row also draws from its own pool so no name
   repeats across the three "For your household" rows either. */
const CHILDCARE_NAMES = ["Naomi", "Agnes", "Collins", "Dorcas", "Vincent", "Mueni", "Loise", "Erick", "Boniface", "Rachel",
  "Sylvia", "Kimani", "Anne", "Godfrey", "Millicent", "Isaac", "Everlyne", "Baraka", "Winfred", "Japheth"];

const TUTORING_NAMES = ["Susan", "Bernard", "Irene", "Duncan", "Christine", "Elias", "Gladys", "Moses", "Sarah", "Timothy",
  "Ruth", "Wilfred", "Diana", "Reuben", "Alfred", "Cecilia", "Emmanuel", "Lilian", "Geoffrey", "Rosemary"];

const CHEF_NAMES = ["Nasra", "Halima", "Dickson", "Zawadi", "Meshack", "Amani", "Titus", "Immaculate", "Oscar", "Betty",
  "Silas", "Nyambura", "Hillary", "Concepta", "Wycliffe", "Felix", "Perpetua", "Mutuku", "Shiku", "Onyango"];

function makeName(namePool, i) {
  return `${namePool[i % namePool.length]} ${String.fromCharCode(65 + (i % 26))}.`;
}

function buildRow(categoryKey, categoryLabel, count, priceRange, namePool, idOffset) {
  return Array.from({ length: count }, (_, i) => {
    const discount = [10, 12, 15, 18, 20, 25, 30][i % 7];
    const original = priceRange[0] + ((i * 137) % (priceRange[1] - priceRange[0]));
    const roundedOriginal = Math.round(original / 50) * 50;
    const current = Math.round((roundedOriginal * (1 - discount / 100)) / 10) * 10;
    return {
      id: idOffset + i + 1,
      name: makeName(namePool, i),
      category: categoryLabel,
      location: LOCATIONS[i % LOCATIONS.length],
      image: IMG[categoryKey][i % IMG[categoryKey].length],
      discount,
      original: roundedOriginal,
      current,
      rating: (4.6 + ((i * 7) % 5) / 10).toFixed(1),
      jobs: 40 + ((i * 53) % 460),
    };
  });
}

const CHILDCARE_DEALS = buildRow("childcare", "Childcare", 20, [300, 700], CHILDCARE_NAMES, 200);
const TUTORING_DEALS  = buildRow("tutoring", "Home Tutoring", 20, [250, 600], TUTORING_NAMES, 300);
const CHEF_DEALS      = buildRow("chef", "Personal Chef", 20, [350, 750], CHEF_NAMES, 400);

function fmt(n) {
  return `KSh ${n.toLocaleString("en-KE")}`;
}

/* Level bands are based on jobs completed:
   Beginner < 25, Junior 25-49, Senior 50-99, Business Pro 100+ */
const LEVELS = {
  beginner:    { label: "Beginner",     bg: "#F1F2F5", color: "#4a5268", border: "#E5E7EB", star: false },
  junior:      { label: "Junior",       bg: "#EAF1FB", color: "#2563AA", border: "#BFDBFE", star: false },
  senior:      { label: "Senior",       bg: "#eef8f3", color: GREEN_DARK, border: "#a7d9bc", star: false },
  businessPro: { label: "Business Pro", bg: AMBER,     color: "#fff",    border: AMBER,     star: true  },
};

function getLevel(jobs) {
  if (jobs >= 100) return LEVELS.businessPro;
  if (jobs >= 50) return LEVELS.senior;
  if (jobs >= 25) return LEVELS.junior;
  return LEVELS.beginner;
}

function Avatar({ src, name, style }) {
  const [err, setErr] = useState(false);
  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  if (err || !src) {
    return (
      <div style={{ ...style, background: "#eef8f3", display: "flex", alignItems: "center", justifyContent: "center", color: GREEN_DARK, fontWeight: 700, fontFamily: SANS }}>
        {initials}
      </div>
    );
  }
  return <img src={src} alt={name} style={style} onError={() => setErr(true)} />;
}

function DealCard({ deal, onBook }) {
  const [hovered, setHovered] = useState(false);
  const level = getLevel(deal.jobs);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onBook(deal)}
      style={{
        width: 158, flexShrink: 0, borderRadius: 14, background: "#fff",
        border: `1px solid ${hovered ? "#a7d9bc" : BORDER}`,
        boxShadow: hovered ? "0 10px 26px rgba(0,0,0,.08)" : "none",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        transition: "all .18s ease", overflow: "hidden", cursor: "pointer",
      }}
    >
      <div style={{ position: "relative", height: 120, background: "#eef1ee" }}>
        <Avatar src={deal.image} name={deal.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        <div style={{ position: "absolute", top: 7, left: 7, background: "#fff", borderRadius: 6, padding: "3px 7px", fontSize: 9.5, fontWeight: 500, color: GREEN_DARK, border: "1px solid #a7d9bc", fontFamily: SANS }}>
          Verified
        </div>
        <div style={{ position: "absolute", top: 7, right: 7, background: AMBER, borderRadius: 6, padding: "3px 7px", fontSize: 9.5, fontWeight: 500, color: "#fff", fontFamily: SANS }}>
          {deal.discount}% off
        </div>
      </div>
      <div style={{ padding: "9px 10px 11px" }}>
        <p style={{ margin: 0, fontSize: 12.5, fontWeight: 500, color: "#0f1117", fontFamily: SANS, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {deal.name}
        </p>
        <p style={{ margin: "2px 0 0", fontSize: 10.5, color: FAINT, fontFamily: SANS, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {deal.category} · {deal.location}
        </p>
        <div style={{ margin: "5px 0 0" }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 3,
            fontSize: 9, fontWeight: 600, color: level.color,
            background: level.bg, border: `1px solid ${level.border}`,
            borderRadius: 5, padding: "2px 6px", fontFamily: SANS,
          }}>
            {level.star && (
              <svg width="7" height="7" viewBox="0 0 20 20" fill="#fff" style={{ flexShrink: 0 }}>
                <path d="M10 1.2l2.7 5.7 6.2.6-4.6 4.2 1.3 6.1L10 14.6l-5.6 3.2 1.3-6.1L1.1 7.5l6.2-.6z" />
              </svg>
            )}
            {level.label}
          </span>
        </div>
        <div style={{ margin: "5px 0 0", display: "flex", alignItems: "center", gap: 4 }}>
          <svg width="10" height="10" viewBox="0 0 20 20" fill={STAR_GOLD} style={{ flexShrink: 0 }}>
            <path d="M10 1.2l2.7 5.7 6.2.6-4.6 4.2 1.3 6.1L10 14.6l-5.6 3.2 1.3-6.1L1.1 7.5l6.2-.6z" />
          </svg>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: "#B8791A", fontFamily: SANS }}>{deal.rating}</span>
          <span style={{ fontSize: 10, color: FAINT, fontFamily: SANS }}>·</span>
          <span style={{ fontSize: 10.5, fontWeight: 600, color: GREEN_DARK, fontFamily: SANS }}>{deal.jobs} jobs</span>
        </div>
        <div style={{ margin: "6px 0 0", display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#0f1117", fontFamily: SANS }}>From {fmt(deal.current)}</span>
          <span style={{ fontSize: 11, color: "#9CA3AF", textDecoration: "line-through", fontFamily: SANS }}>{fmt(deal.original)}</span>
        </div>
      </div>
    </div>
  );
}

/* ── Book button — rendered as its own element below the card, not
       inside it, but pinned to the same 158px width. ── */
function BookButton({ deal, inCart, onToggleCart }) {
  return (
    <button
      onClick={() => onToggleCart(deal)}
      style={{
        marginTop: 8, width: 158, height: 32, borderRadius: 8,
        border: `1.5px solid ${inCart ? GREEN : BORDER}`,
        background: inCart ? GREEN : "#fff",
        color: inCart ? "#fff" : GREEN_DARK,
        fontFamily: SERIF, fontSize: 13.5, fontWeight: 500,
        cursor: "pointer", transition: "all .15s ease", flexShrink: 0,
      }}
    >
      {inCart ? "Booked ✓" : "Book"}
    </button>
  );
}

/* ── Row — identical structure/behavior to JiraniDeals' DealsRow:
       header in a padded column, track bleeds full-width past it. ── */
function DealsRow({ title, deals, onBook, cartIds, onToggleCart, padX, marginBottom = 30 }) {
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", update, { passive: true });
    update();
    return () => el.removeEventListener("scroll", update);
  }, [update, deals]);

  const scroll = (dir) => scrollRef.current?.scrollBy({ left: dir * 480, behavior: "smooth" });

  return (
    <div style={{ marginBottom }}>
      <div style={{ width: "100%", padding: `0 ${padX}px` }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 18, gap: 16 }}>
          <h3 style={{ fontFamily: SERIF, fontSize: "clamp(24px,3.2vw,32px)", fontWeight: 400, color: "#0f1117", lineHeight: 1.08, letterSpacing: "-0.03em", margin: 0 }}>
            {title}
          </h3>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => scroll(-1)} disabled={!canLeft} style={arrowStyle(canLeft)} aria-label="Scroll left">‹</button>
            <button onClick={() => scroll(1)} disabled={!canRight} style={arrowStyle(canRight)} aria-label="Scroll right">›</button>
          </div>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="fyh-track"
        style={{ overflowX: "auto", paddingBottom: 4, paddingLeft: padX, paddingRight: padX, width: "100%" }}
      >
        <div style={{ display: "flex", gap: 12, width: "max-content" }}>
          {deals.map(deal => (
            <div key={deal.id} style={{ display: "flex", flexDirection: "column", flexShrink: 0 }}>
              <DealCard deal={deal} onBook={onBook} />
              <BookButton deal={deal} inCart={cartIds.has(deal.id)} onToggleCart={onToggleCart} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function arrowStyle(enabled) {
  return {
    width: 32, height: 32, borderRadius: "50%", border: `1.5px solid ${BORDER}`,
    background: "#fff", fontSize: 16, color: "#2d3549", cursor: enabled ? "pointer" : "default",
    opacity: enabled ? 1 : 0.3, lineHeight: 1,
  };
}

export default function ForYourHousehold() {
  const navigate = useNavigate();
  const { isMobile, isTablet } = useBreakpoint();
  const padX = isMobile ? 16 : isTablet ? 20 : 32;
  const onBook = useCallback((deal) => navigate(`/pro/${deal.id}?book=1&promo=1`), [navigate]);

  // Standalone cart state for now, same pattern as JiraniDeals — swap for
  // your real cart context/store later; onToggleCart is the only thing
  // that needs rewiring.
  const [cartIds, setCartIds] = useState(() => new Set());
  const onToggleCart = useCallback((deal) => {
    setCartIds(prev => {
      const next = new Set(prev);
      if (next.has(deal.id)) next.delete(deal.id);
      else next.add(deal.id);
      return next;
    });
  }, []);

  return (
    <section style={{ background: SECTION_BG, padding: "0 0 8px", width: "100%", overflow: "hidden" }}>
      <style>{`.fyh-track::-webkit-scrollbar{display:none}.fyh-track{-ms-overflow-style:none;scrollbar-width:none}`}</style>

      {/* Section header — sits in the same padded column as each row's own header */}
      <div style={{ width: "100%", padding: `0 ${padX}px`, marginBottom: 4 }}>
        <h2 style={{
          fontFamily: SERIF, fontWeight: 400, color: "#0f1117",
          fontSize: "clamp(30px,4.4vw,42px)", lineHeight: 1.05,
          letterSpacing: "-0.03em", margin: "0 0 20px",
        }}>
          For your <em style={{ fontStyle: "italic", color: GREEN }}>household</em>
        </h2>
      </div>

      <DealsRow
        title="Childcare"
        deals={CHILDCARE_DEALS} onBook={onBook} cartIds={cartIds} onToggleCart={onToggleCart} padX={padX}
      />
      <DealsRow
        title="Home Tutoring"
        deals={TUTORING_DEALS} onBook={onBook} cartIds={cartIds} onToggleCart={onToggleCart} padX={padX}
      />
      <DealsRow
        title="Personal Chefs"
        deals={CHEF_DEALS} onBook={onBook} cartIds={cartIds} onToggleCart={onToggleCart} padX={padX} marginBottom={0}
      />
    </section>
  );
}